import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button, Input } from '@/components/atoms'
import { portfolioTrackerService } from '@/services/portfolioTrackerService'
import { projectService } from '@/services/projectService'
import { ProjectSearchResult } from '@/types/project'

// Helper function to format date as mm/dd/yyyy
const formatDateForInput = (date: string | Date | null | undefined): string => {
  if (!date) return ''
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return ''
  
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const year = d.getFullYear()
  
  return `${month}/${day}/${year}`
}

// Helper function to parse mm/dd/yyyy format back to ISO string
const parseDateFromInput = (dateString: string): string => {
  if (!dateString) return ''
  
  const [month, day, year] = dateString.split('/')
  if (!month || !day || !year) return ''
  
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
  if (isNaN(date.getTime())) return ''
  
  return date.toISOString().split('T')[0]
}

interface EditAssetModalProps {
  isOpen: boolean
  onClose: () => void
  portfolioId: string
  assetId: string
  assetData: {
    project_name?: string
    date: string
    invested_amount: number
    valuation?: number
    equity_or_tokens_amount?: string | number
    terms: string
    project_website?: string
    project_id?: string
    project?: {
      name: string
      logo?: string
      project_id: string
    }
  }
  onSuccess?: () => void
}

export const EditAssetModal: React.FC<EditAssetModalProps> = ({
  isOpen,
  onClose,
  assetId,
  assetData,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    project_name: assetData.project?.name || assetData.project_name || '',
    date: formatDateForInput(assetData.date),
    invested_amount: assetData.invested_amount?.toString() || '',
    valuation: assetData.valuation?.toString() || '',
    equity_or_tokens_amount: assetData.equity_or_tokens_amount?.toString() || '',
    terms: assetData.terms || '',
    project_website: assetData.project_website || ''
  })

  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<ProjectSearchResult[]>([])
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [selectedProject, setSelectedProject] = useState<ProjectSearchResult | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Initialize form data when assetData changes
  useEffect(() => {
    console.log('EditAssetModal - assetData received:', assetData)
    console.log('EditAssetModal - assetData.date:', assetData.date)
    console.log('EditAssetModal - assetData.invested_amount:', assetData.invested_amount)
    console.log('EditAssetModal - assetData.valuation:', assetData.valuation)
    
    setFormData({
      project_name: assetData.project?.name || assetData.project_name || '',
      date: formatDateForInput(assetData.date),
      invested_amount: assetData.invested_amount?.toString() || '',
      valuation: assetData.valuation?.toString() || '',
      equity_or_tokens_amount: assetData.equity_or_tokens_amount?.toString() || '',
      terms: assetData.terms || '',
      project_website: assetData.project_website || ''
    })
  }, [assetData])

  // Search for projects
  const searchProjects = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([])
      setShowSearchResults(false)
      return
    }
    
    try {
      setSearchLoading(true)
      const results = await projectService.searchProjects(query)
      setSearchResults(results)
      setShowSearchResults(true)
    } catch (err) {
      console.error('Error searching projects:', err)
    } finally {
      setSearchLoading(false)
    }
  }

  // Debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchQuery.trim()) {
        searchProjects(searchQuery)
      } else {
        setSearchResults([])
        setShowSearchResults(false)
      }
    }, 300)
    
    return () => {
      clearTimeout(handler)
    }
  }, [searchQuery])

  // Handle project selection
  const handleSelectProject = (project: ProjectSearchResult) => {
    setSelectedProject(project)
    setFormData(prev => ({
      ...prev,
      project_name: project.project_name || project.name || ''
    }))
    setSearchQuery(project.project_name || project.name || '')
    setShowSearchResults(false)
  }

  // Handle clearing selected project
  const handleClearProject = () => {
    setSelectedProject(null)
    setFormData(prev => ({
      ...prev,
      project_name: ''
    }))
    setSearchQuery('')
    setShowSearchResults(false)
  }

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    
    if (name === 'project_name') {
      setSearchQuery(value)
      setSelectedProject(null) // Clear selected project when typing
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.project_name.trim()) {
      setError('Project name is required')
      return
    }
    
    if (!formData.date) {
      setError('Date is required')
      return
    }
    
    if (!formData.invested_amount) {
      setError('Invested amount is required')
      return
    }
    
    if (!formData.terms.trim()) {
      setError('Terms are required')
      return
    }
    
    try {
      setSubmitting(true)
      setError(null)
      
             const apiData = {
         project_name: formData.project_name,
         date: parseDateFromInput(formData.date),
         invested_amount: parseFloat(formData.invested_amount),
         valuation: formData.valuation ? parseFloat(formData.valuation) : 0,
         equity_or_tokens_amount: formData.equity_or_tokens_amount ? parseFloat(formData.equity_or_tokens_amount) : 0,
         terms: formData.terms,
         project_website: formData.project_website || undefined,
         project_id: selectedProject?.project_id || assetData.project?.project_id || assetData.project_id
       }
      
      await portfolioTrackerService.updateAsset(assetId, apiData)
      
      onSuccess?.()
      onClose()
    } catch (err) {
      setError('Failed to update asset. Please try again.')
      console.error('Error updating asset:', err)
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>Edit Asset</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Project Name with Search */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Project Name*
              </label>
              <div className="relative">
                {selectedProject && (
                  <div className="flex items-center gap-2 p-2 border rounded-lg bg-gray-50">
                    {selectedProject.logo && (
                      <img 
                        src={selectedProject.logo} 
                        alt=""
                        className="w-6 h-6 rounded"
                      />
                    )}
                    <span className="flex-1">{selectedProject.project_name || selectedProject.name}</span>
                    <button 
                      type="button"
                      onClick={handleClearProject}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      ×
                    </button>
                  </div>
                )}
                {!selectedProject && (
                  <Input
                    name="project_name"
                    value={formData.project_name}
                    onChange={handleInputChange}
                    placeholder="Enter project name or search"
                    required
                  />
                )}
                {searchLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  </div>
                )}
              </div>
              
              {showSearchResults && searchResults.length > 0 && !selectedProject && (
                <div className="mt-1 border rounded-lg bg-white shadow-lg max-h-40 overflow-y-auto">
                  {searchResults.map(project => (
                    <div 
                      key={project.project_id}
                      className="flex items-center gap-2 p-2 hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleSelectProject(project)}
                    >
                      {project.logo && (
                        <img 
                          src={project.logo} 
                          alt=""
                          className="w-6 h-6 rounded"
                        />
                      )}
                      <span>{project.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Investment Date*
              </label>
              <Input
                name="date"
                type="text"
                value={formData.date}
                onChange={handleInputChange}
                placeholder="mm/dd/yyyy"
                required
              />
            </div>

            {/* Invested Amount */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Invested Amount (USD)*
              </label>
              <Input
                name="invested_amount"
                type="text"
                value={formData.invested_amount}
                onChange={handleInputChange}
                placeholder="50000 or 50k, 1.5M, 2B"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Supports formats: 1000, 50k, 1.5M, 2B</p>
            </div>

            {/* Valuation */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Project Valuation (USD)
              </label>
              <Input
                name="valuation"
                type="text"
                value={formData.valuation}
                onChange={handleInputChange}
                placeholder="1000000 or 1M, 5B"
              />
              <p className="text-xs text-gray-500 mt-1">Supports formats: 1000000, 1M, 5B</p>
            </div>

            {/* Equity/Tokens Amount */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Equity/Token Amount
              </label>
              <Input
                name="equity_or_tokens_amount"
                type="text"
                value={formData.equity_or_tokens_amount}
                onChange={handleInputChange}
                placeholder="100000 or 100k, 0.05 (5%)"
              />
              <p className="text-xs text-gray-500 mt-1">Supports formats: 100000, 100k, 0.05 for 5%</p>
            </div>

            {/* Terms */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Terms*
              </label>
              <textarea
                name="terms"
                value={formData.terms}
                onChange={handleInputChange}
                placeholder="SAFE agreement, equity, tokens, etc."
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                rows={3}
                required
              />
            </div>

            {/* Project Website */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Project Website
              </label>
              <Input
                name="project_website"
                type="text"
                value={formData.project_website}
                onChange={handleInputChange}
                placeholder="https://example.com"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
                type="button"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={submitting}
                className="flex-1"
              >
                {submitting ? 'Updating...' : 'Update Asset'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

export default EditAssetModal
